import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Send, Sparkles, Flag, Lock, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function Chat() {
  const { id } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState("coach");
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(id || null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  // ai coach
  const [coach, setCoach] = useState([]);
  const [coachInput, setCoachInput] = useState("");
  const [coachBusy, setCoachBusy] = useState(false);
  const sessionId = useRef("coach-" + Date.now());
  const endRef = useRef(null);
  const [model, setModel] = useState("claude");

  const MODELS = [
    { id: "claude", label: "Claude" },
    { id: "chatgpt", label: "ChatGPT" },
    { id: "gemini", label: "Gemini" },
  ];

  const loadThreads = useCallback(async () => { const { data } = await api.get("/threads"); setThreads(data.threads); }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { if (id) { setActive(id); setTab("messages"); } }, [id]);
  useEffect(() => {
    if (!active) return;
    api.get(`/messages/${active}`).then(({ data }) => setMessages(data.messages));
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, coach]);

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      const { data } = await api.post("/messages", { recipient_id: active, text });
      setMessages([...messages, data]); setText("");
      if (data.flagged) toast.warning("Heads up: this message was flagged by our safety filter.");
      loadThreads();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const askCoach = async (preset) => {
    const q = preset || coachInput;
    if (!q.trim()) return;
    setCoach((c) => [...c, { role: "user", content: q }]);
    setCoachInput(""); setCoachBusy(true);
    try {
      const { data } = await api.post("/ai/coach", { session_id: sessionId.current, message: q, model });
      setCoach((c) => [...c, { role: "assistant", content: data.reply }]);
      if (data.flagged) toast.warning("The coach flagged this. Keep it kind and platonic 💛");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setCoachBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-sand">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-8">
        <h1 className="font-display text-4xl font-black text-charcoal mb-2">Chat</h1>
        <p className="text-charcoal/60 mb-6 flex items-center gap-1.5 text-sm"><Lock className="w-4 h-4 text-sage" /> Messages are private. Reported messages can be submitted to moderators without exposing others.</p>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-full bg-white border border-border p-1">
            <TabsTrigger value="coach" data-testid="tab-coach" className="rounded-full data-[state=active]:bg-terracotta data-[state=active]:text-white"><Sparkles className="w-4 h-4 mr-1" /> AI Coach</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" className="rounded-full data-[state=active]:bg-terracotta data-[state=active]:text-white"><MessageCircle className="w-4 h-4 mr-1" /> Messages</TabsTrigger>
          </TabsList>

          <TabsContent value="coach">
            <div className="bg-white rounded-2xl border border-border mt-4 flex flex-col h-[60vh]">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {coach.length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 rounded-full bg-sun/30 flex items-center justify-center mx-auto mb-3"><Sparkles className="w-6 h-6 text-charcoal" /></div>
                    <p className="font-display text-xl font-bold text-charcoal">Conversation & Consent Coach</p>
                    <p className="text-sm text-charcoal/60 mt-1 max-w-sm mx-auto">Warm icebreakers & boundary-aware tips. Try one:</p>
                    <div className="flex flex-wrap gap-2 justify-center mt-4">
                      {["Give me a fun icebreaker for a coffee meet", "How do I respect someone's boundaries?", "Suggest topics for a walk in the park"].map((p) => (
                        <button key={p} data-testid="coach-preset" onClick={() => askCoach(p)} className="text-sm bg-sand hover:bg-sun/20 text-charcoal/80 px-3 py-1.5 rounded-full border border-border transition-colors duration-200">{p}</button>
                      ))}
                    </div>
                  </div>
                )}
                {coach.map((m, i) => (
                  <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                    {m.role === "assistant" && <div className="w-8 h-8 rounded-full bg-sun flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4 text-charcoal" /></div>}
                    <div className={`rounded-2xl p-3.5 text-sm max-w-md ${m.role === "user" ? "bg-terracotta text-white rounded-tr-sm" : "bg-sun/15 text-charcoal rounded-tl-sm"}`}>{m.content}</div>
                  </div>
                ))}
                {coachBusy && <div className="flex gap-3"><div className="w-8 h-8 rounded-full bg-sun flex items-center justify-center"><Sparkles className="w-4 h-4 text-charcoal animate-pulse" /></div><div className="bg-sun/15 rounded-2xl p-3.5 text-sm text-charcoal/50">Thinking...</div></div>}
                <div ref={endRef} />
              </div>
              <div className="border-t border-border p-3 flex gap-2 items-center">
                <div className="flex gap-1 bg-sand rounded-full p-1" data-testid="coach-model-picker">
                  {MODELS.map((m) => (
                    <button key={m.id} data-testid={`coach-model-${m.id}`} onClick={() => setModel(m.id)}
                      className={`text-xs font-semibold px-2.5 py-1.5 rounded-full transition-colors duration-200 ${model === m.id ? "bg-terracotta text-white" : "text-charcoal/60 hover:text-charcoal"}`}>{m.label}</button>
                  ))}
                </div>
                <Input data-testid="coach-input" value={coachInput} onChange={(e) => setCoachInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && askCoach()} placeholder="Ask the coach anything..." className="rounded-full border-border" />
                <Button data-testid="coach-send" onClick={() => askCoach()} disabled={coachBusy} className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white px-4"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="messages">
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              <div className="bg-white rounded-2xl border border-border p-3 h-[60vh] overflow-y-auto">
                {threads.length === 0 ? <p className="text-sm text-charcoal/50 p-3">No conversations yet. Message a host from their profile.</p> :
                  threads.map((t) => (
                    <button key={t.thread_id} data-testid={`thread-${t.other_id}`} onClick={() => setActive(t.other_id)}
                      className={`w-full text-left p-3 rounded-xl flex items-center gap-3 transition-colors duration-200 ${active === t.other_id ? "bg-terracotta/10" : "hover:bg-sand"}`}>
                      <img src={t.other_avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${t.other_name}`} className="w-10 h-10 rounded-full object-cover" alt="" />
                      <div className="min-w-0"><p className="font-semibold text-sm text-charcoal truncate">{t.other_name}</p><p className="text-xs text-charcoal/50 truncate">{t.last}</p></div>
                    </button>
                  ))}
              </div>
              <div className="md:col-span-2 bg-white rounded-2xl border border-border flex flex-col h-[60vh]">
                {!active ? <div className="flex-1 flex items-center justify-center text-charcoal/40 text-sm">Select a conversation</div> : (<>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.map((m) => (
                      <div key={m.id} className={`flex ${m.sender_id === user.id ? "justify-end" : ""}`}>
                        <div className="group relative">
                          <div className={`rounded-2xl p-3 text-sm max-w-xs ${m.sender_id === user.id ? "bg-terracotta text-white rounded-tr-sm" : "bg-sand text-charcoal rounded-tl-sm"}`}>{m.text}</div>
                          {m.sender_id !== user.id && (
                            <button onClick={async () => { await api.post(`/messages/${m.id}/report`); toast.success("Reported to moderators"); }}
                              className="absolute -right-6 top-1 opacity-0 group-hover:opacity-100 text-charcoal/40 hover:text-signal transition-opacity"><Flag className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={endRef} />
                  </div>
                  <div className="border-t border-border p-3 flex gap-2">
                    <Input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Keep it kind & platonic..." className="rounded-full border-border" />
                    <Button data-testid="message-send" onClick={send} className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white px-4"><Send className="w-4 h-4" /></Button>
                  </div>
                </>)}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
